import type { IconName } from '@/shared/ui';

/** Help topic of the reports list and of every report (one section per report). */
export const REPORTS_HELP = '/help/screens/subscription-reports';

/** `?export=pdf`: the reports list asked for the PDF; it is saved once every section has loaded. */
export const EXPORT_PARAM = 'export';

export type ReportId =
  | 'executive-summary'
  | 'mrr-movements'
  | 'churn-and-retention'
  | 'renewals-outlook'
  | 'trials-and-conversion'
  | 'plans-and-offers';

/** A management report of the Subscriptions reports list. */
export interface ReportDef {
  id: ReportId;
  title: string;
  /** One sentence: what the report answers, shown on its tile. */
  summary: string;
  /** The report's sections, in order: the tile lists them as its contents. */
  sections: string[];
  /** Whether the global date range applies (Renewals look ahead from today instead). */
  usesRange: boolean;
  icon: IconName;
}

/** Management reports in the order of the reports list. */
export const REPORTS: readonly ReportDef[] = [
  {
    id: 'executive-summary',
    title: 'Executive summary',
    summary:
      'Revenue, customers and retention of the period on one page, against the period before.',
    sections: [
      'Key figures',
      'MRR per month',
      'MRR bridge',
      'Billing mix',
      'Subscriptions by state',
    ],
    usesRange: true,
    icon: 'overview',
  },
  {
    id: 'mrr-movements',
    title: 'MRR movements',
    summary:
      'Where MRR came from and where it went, month by month, and the largest account movements.',
    sections: ['Movement totals', 'Monthly bridge', 'Largest movements in the last month'],
    usesRange: true,
    icon: 'revenue',
  },
  {
    id: 'churn-and-retention',
    title: 'Churn and retention',
    summary: 'Subscriptions and MRR lost, retention of the paying base and of each start cohort.',
    sections: [
      'Churn figures',
      'Churned MRR per month',
      'Lost accounts in the last month',
      'Cohort retention',
    ],
    usesRange: true,
    icon: 'cohorts',
  },
  {
    id: 'renewals-outlook',
    title: 'Renewals outlook',
    summary: 'Yearly plans and contracts renewing in the next 12 months, and the renewals at risk.',
    sections: [
      'Renewal figures',
      'Renewal value by month',
      'Renewals at risk',
      'Largest renewals in 90 days',
    ],
    usesRange: false,
    icon: 'calendar',
  },
  {
    id: 'trials-and-conversion',
    title: 'Trials and conversion',
    summary: 'Trials started in the period, how many converted, and the plans they converted to.',
    sections: ['Trial figures', 'Trials per start month', 'Converted to'],
    usesRange: true,
    icon: 'funnel',
  },
  {
    id: 'plans-and-offers',
    title: 'Plans and offers',
    summary: 'Revenue, prices and retention per offer, and the add-ons sold with them.',
    sections: ['Offer figures', 'Revenue by offer', 'Add-ons'],
    usesRange: true,
    icon: 'offers',
  },
];

export const reportById = (id: string | undefined): ReportDef | undefined =>
  REPORTS.find((report) => report.id === id);
